import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from 'react-i18next';
import { baseURL } from "../services/APIService";
const Slider = ({ data, title, variant = 'projects' }) => {
  // Define the base URL for API calls
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(3);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Determine number of slides based on screen width
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setSlidesToShow(1);
      } else if (window.innerWidth < 1024) {
        setSlidesToShow(2);
      } else {
        setSlidesToShow(3);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    // Auto advance slides
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (Array.isArray(data) && data.length > 0 ? (prevIndex + 1) % data.length : 0));
    }, 5000);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.length]);
  
  // Function to move to the next slide
  const nextSlide = () => {
    if (Array.isArray(data) && data.length > 0) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % data.length);
    }
  };
  
  // Function to move to the previous slide
  const prevSlide = () => {
    if (Array.isArray(data) && data.length > 0) {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + data.length) % data.length);
    }
  };
  
  // Get visible slides based on current index
  const getVisibleSlides = () => {
    if (!Array.isArray(data) || data.length === 0) return [];
  
    
    const visibleSlides = [];
    for (let i = 0; i < slidesToShow; i++) {
      const slideIndex = (currentIndex + i) % data.length;
      
      const slideItem = {
        ...data[slideIndex],
        index: slideIndex
      };
      
      // Ensure image URL is properly set for success stories with hasImage
      if (variant === 'stories' && !slideItem.image && slideItem.hasImage) {
        slideItem.image = `${baseURL}/stories/image/${slideItem.id}`;
      }
      
      visibleSlides.push(slideItem);
    }
    return visibleSlides;
  };

  return (
    <div id="projects-slider" className="max-w-screen-xl mx-auto px-4 xl:pt-[120px] lg:pt-[0px] md:pt-[65px] sm:pt-[48px] pt-[32px]">
      {/* Title with decorative elements */}
      <div className="text-center mb-10">
        <h2 className="text-center text-gray-800 font-bold md:text-3xl sm:text-2xl text-xl max-w-[600px] mx-auto">
          {title || t('project_title', 'Our Projects')}
        </h2>
        <div className="flex items-center justify-center mt-3">
          <div className="h-[2px] w-8 bg-blue-500"></div>
          <div className="h-1 w-1 rounded-full bg-blue-500 mx-2"></div>
          <div className="h-[2px] w-16 bg-blue-500"></div>
          <div className="h-1 w-1 rounded-full bg-blue-500 mx-2"></div>
          <div className="h-[2px] w-8 bg-blue-500"></div>
        </div>
      </div>
      
      {/* Slider container with navigation */}
      <div className="relative overflow-hidden px-4 py-8">
        {/* Navigation buttons */}
        <button 
          onClick={prevSlide}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/70 hover:bg-white p-2 rounded-full shadow-md hover:shadow-lg transition-all duration-300"
          aria-label="Previous slide"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button 
          onClick={nextSlide}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/70 hover:bg-white p-2 rounded-full shadow-md hover:shadow-lg transition-all duration-300"
          aria-label="Next slide"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        
        {/* Slides */}
        <div className="flex gap-4 lg:gap-6 py-4">
          <AnimatePresence mode="wait">
            {getVisibleSlides().map((item, idx) => (
              <motion.div
                key={`${item.index}-${idx}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
                className="flex-1 min-w-0"
              >
                <div 
                  className={`bg-white flex flex-col border-2 border-gray-200 hover:border-gray-300 rounded-xl px-5 py-6 
                    shadow-md hover:shadow-xl transition-all duration-300 h-full transform hover:-translate-y-1`}
                >
                  <div className="mb-4 overflow-hidden rounded-lg">
                    <img 
                      src={item.image || (item.hasImage ? `${baseURL}/stories/image/${item.id}` : '/assets/placeholder-image.jpg')} 
                      alt={item.name || item.title} 
                      className="w-full h-48 object-cover rounded-md transform hover:scale-105 transition-transform duration-500" 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/assets/placeholder-image.jpg';
                      }}
                    />
                  </div>
                  <h3 className="font-bold text-xl text-gray-800 mb-2 line-clamp-1">
                    {item.name || item.title}
                  </h3>
                  <div className="w-12 h-1 bg-blue-500 mb-3 transition-all duration-300"></div>
                  <p className="text-gray-600 flex-grow line-clamp-3">
                    {item.description || item.content}
                  </p>
                  
                  <div className="mt-4 pt-2 border-t border-gray-100 flex justify-end">
                    <button 
                      onClick={() => setSelectedProject(item)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 group"
                    >
                      {t('read_more', 'Read more')}
                      <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        
        {/* Indicators */}
        {Array.isArray(data) && data.length > slidesToShow && (
          <div className="flex justify-center gap-2 mt-8">
            {data.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === currentIndex ? "bg-blue-600 w-6" : "bg-gray-300 hover:bg-gray-400"
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Project Details Modal */}
      <AnimatePresence>
        {selectedProject && (
          <>
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              onClick={() => setSelectedProject(null)}
            />
            
            {/* Modal */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3, type: "spring", bounce: 0.2 }}
              className="fixed left-0 right-0 top-[5vh] md:top-[10vh] z-50
                mx-auto w-[95%] md:w-[90%] lg:w-[80%] max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl
                bg-white shadow-2xl border border-gray-200"
              style={{ maxHeight: 'calc(90vh - 20px)' }}
            >
              {/* Modal header */}
              <div className="relative">
                <img 
                  src={selectedProject.image || (selectedProject.hasImage ? `${baseURL}/stories/image/${selectedProject.id}` : '/assets/placeholder-image.jpg')} 
                  alt={selectedProject.name || selectedProject.title} 
                  className="w-full h-56 sm:h-64 object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/assets/placeholder-image.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                
                {/* Close button */}
                <button 
                  className="absolute top-4 right-4 bg-white/80 hover:bg-white p-2 rounded-full 
                    shadow-md hover:shadow-lg transition-all duration-300"
                  onClick={() => setSelectedProject(null)}
                  aria-label="Close modal"
                >
                  <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                {/* Title on image */}
                <div className="absolute bottom-4 left-6 right-6">
                  <h3 className="text-white text-2xl sm:text-3xl font-bold">{selectedProject.name || selectedProject.title}</h3>
                </div>
              </div>
              
              {/* Modal content */}
              <div className="p-6">
                <div className="w-16 h-1 bg-blue-500 mb-6"></div>
                
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 mb-6">{selectedProject.description || selectedProject.content}</p>
                  
                  {(selectedProject.details || selectedProject.person_name) && (
                    <div className="mt-4">
                      <h4 className="text-xl font-semibold mb-3">Additional Information</h4>
                      {selectedProject.details && <p className="text-gray-700">{selectedProject.details}</p>}
                      {selectedProject.person_name && (
                        <p className="text-gray-700 mt-2">
                          <strong>Person:</strong> {selectedProject.person_name}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Action buttons */}
                <div className="mt-8 flex flex-wrap gap-3 border-t border-gray-200 pt-4">
                  <button 
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg 
                      transition-colors duration-300 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {t('get_involved', 'Get Involved')}
                  </button>
                  
                  <button 
                    className="px-6 py-3 border-2 border-gray-300 hover:border-gray-400 text-gray-700 
                      font-medium rounded-lg transition-colors duration-300 flex items-center gap-2"
                    onClick={() => setSelectedProject(null)}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {t('close', 'Close')}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Slider;
